import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ImageCropField from '@/components/ImageCropField';
import { EMPTY_SERVICE_ICON, SERVICE_ICONS, isServiceIconName, type ServiceIcon } from '@/config/serviceIcons';

export function ServiceIconPreview({ value }: { value: ServiceIcon }) {
  if (value.mode === 'upload' && value.imageUrl) {
    return <img src={value.imageUrl} alt="" className="size-10 object-contain" />;
  }
  if (value.mode === 'library' && value.name) {
    const Icon = SERVICE_ICONS[value.name].icon;
    return <Icon className="size-8 text-primary" aria-hidden="true" />;
  }
  return <span className="text-xs text-muted-foreground">No icon</span>;
}

export default function ServiceIconField({
  value,
  onChange,
}: {
  value: ServiceIcon;
  onChange: (value: ServiceIcon) => void;
}) {
  const [query, setQuery] = useState('');
  const uploadGeneration = useRef(0);
  const mounted = useRef(false);
  const generation = uploadGeneration.current;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const searchId = useId();
  const search = query.trim().toLowerCase();
  const names = Object.keys(SERVICE_ICONS).filter(isServiceIconName).filter((name) =>
    `${name} ${SERVICE_ICONS[name].label}`.toLowerCase().includes(search));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Service icon source">
        {(['none', 'library', 'upload'] as const).map((mode) => (
          <Button
            key={mode}
            type="button"
            variant={value.mode === mode ? 'default' : 'outline'}
            aria-pressed={value.mode === mode}
            onClick={() => {
              if (value.mode !== mode) {
                uploadGeneration.current += 1;
                onChange({ ...EMPTY_SERVICE_ICON, mode });
              }
            }}
          >
            {mode === 'none' ? 'No icon' : mode === 'library' ? 'Icon library' : 'Upload icon'}
          </Button>
        ))}
      </div>
      {value.mode === 'library' && (
        <div className="space-y-3">
          <label htmlFor={searchId} className="sr-only">Search service icons</label>
          <Input id={searchId} placeholder="Search icons..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
            {names.map((name) => {
              const { icon: Icon, label } = SERVICE_ICONS[name];
              return (
                <Button
                  key={name}
                  type="button"
                  variant={value.name === name ? 'default' : 'outline'}
                  className="h-auto min-h-16 flex-col gap-1 px-2 py-3"
                  aria-pressed={value.name === name}
                  onClick={() => onChange({ mode: 'library', name, imageUrl: '' })}
                >
                  <Icon className="size-5" aria-hidden="true" />
                  <span className="text-[10px]">{label}</span>
                </Button>
              );
            })}
          </div>
          {names.length === 0 && <p className="text-sm text-muted-foreground">No matching icons.</p>}
          <div className="flex items-center gap-3 rounded-lg border border-border p-3">
            <ServiceIconPreview value={value} />
            <span className="text-sm">{value.name ? SERVICE_ICONS[value.name].label : 'Select an icon above'}</span>
          </div>
        </div>
      )}
      {value.mode === 'upload' && (
        <ImageCropField
          value={value.imageUrl}
          onChange={(imageUrl) => {
            // Ignore uploads that finish after switching modes or closing the dialog.
            if (mounted.current && generation === uploadGeneration.current) {
              onChange({ mode: 'upload', name: '', imageUrl });
            }
          }}
          aspectRatio={null}
          maxSizeMB={2}
          hint="Upload a PNG or JPEG (up to 2 MB). Transparent PNGs work well. Uploaded icons keep their original colors."
        />
      )}
    </div>
  );
}
